// Centralized API Service for Lead Capture
// All forms site-wide use this single script for geolocation + lead submission.

const GEOLOCATION_API_URL = 'https://validationapi.thetechmenders.com/api/GeoLocation/GetGeoLocation';
const LEAD_API_URL = 'https://leadapi.crmbytes.com/api/Lead/AddLead';

// Country code ↔ dial code mapping (shared across all forms)
const COUNTRY_DIAL_MAP = {
  'US': '+1', 'CA': '+1', 'GB': '+44', 'IN': '+91', 'AU': '+61',
  'DE': '+49', 'FR': '+33', 'JP': '+81', 'CN': '+86', 'KR': '+82',
  'BR': '+55', 'MX': '+52', 'IT': '+39', 'ES': '+34', 'NL': '+31',
  'SE': '+46', 'CH': '+41', 'SG': '+65', 'AE': '+971', 'ZA': '+27',
  'NZ': '+64', 'PK': '+92', 'RU': '+7', 'TR': '+90', 'SA': '+966',
  'EG': '+20', 'NG': '+234', 'KE': '+254', 'PH': '+63', 'TH': '+66',
  'MY': '+60', 'ID': '+62', 'VN': '+84', 'BD': '+880', 'LK': '+94',
  'IE': '+353', 'PT': '+351', 'GR': '+30', 'PL': '+48', 'CZ': '+420',
  'AT': '+43', 'DK': '+45', 'NO': '+47', 'FI': '+358', 'BE': '+32',
  'HK': '+852', 'TW': '+886', 'IL': '+972', 'CL': '+56', 'CO': '+57',
  'AR': '+54', 'PE': '+51', 'QA': '+974', 'KW': '+965', 'BH': '+973',
  'OM': '+968', 'JO': '+962'
};

const DIAL_COUNTRY_MAP = Object.entries(COUNTRY_DIAL_MAP).reduce((acc, [country, dial]) => {
  const code = parseInt(dial.replace('+', ''));
  if (!acc[code]) acc[code] = country;
  return acc;
}, {});

/**
 * Get dial code from country code (e.g. 'PK' → '+92')
 */
export const getDialCodeFromCountry = (countryCode) => {
  return COUNTRY_DIAL_MAP[countryCode] || null;
};

/**
 * Get country code from dial code number (e.g. 92 → 'PK')
 */
const getCountryFromDialCode = (dialCode) => {
  return DIAL_COUNTRY_MAP[dialCode] || 'US';
};

/**
 * Fetch user's geolocation data
 */
export const fetchGeolocation = async () => {
  try {
    const response = await fetch(GEOLOCATION_API_URL, {
      method: 'GET',
      headers: { 'accept': 'text/plain' }
    });
    if (!response.ok) throw new Error('Failed to fetch geolocation');
    return await response.json();
  } catch (error) {
    console.error('Geolocation API Error:', error);
    return null;
  }
};

/**
 * Submit lead form data to CRM API
 * @param {Object} formData - { name, email, phone, country_code, message, packageTitle?, packageAmount? }
 * @param {Object|null} geoData - Geolocation response object
 */
export const submitLead = async (formData, geoData = null) => {
  try {
    // Only numeric digits for phone
    const phoneNumber = formData.phone.replace(/\D/g, '');

    // Dial code as integer (e.g. '+92' → 92)
    const countryDialCode = parseInt(formData.country_code.replace('+', ''));

    // Country: prefer geolocation countryCode, fallback to dial-code mapping
    const country = geoData?.data?.countryCode || getCountryFromDialCode(countryDialCode);

    // Use a fallback IP if geolocation didn't provide one
    // This prevents 400 errors from the API which requires IP address
    const ipAddress = geoData?.data?.ipAddress || '0.0.0.0';

    const payload = {
      name: formData.name,
      email: formData.email,
      countryDialCode: countryDialCode,
      country: country,
      phoneNumber: phoneNumber,
      ipAddress: ipAddress,
      description: formData.message || '',
      pageUrl: window.location.href,
      city: geoData?.data?.cityName || '',
      state: geoData?.data?.regionName || '',
      geoLocation: geoData ? JSON.stringify(geoData) : '',
      packageTitle: formData.packageTitle || '',
      packageAmount: formData.packageAmount || 0
    };

    const response = await fetch(LEAD_API_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'accept': 'application/json'
      },
      body: JSON.stringify(payload)
    });

    // Read body only once - get text first then parse
    const responseText = await response.text();
    let data;
    
    try {
      data = responseText ? JSON.parse(responseText) : {};
    } catch (parseError) {
      console.error('Failed to parse JSON response:', responseText);
      data = { errorMessage: responseText || 'Invalid response from server' };
    }

    // Handle different response formats
    // API returns HTTP 200 with statusCode in body for auth errors
    // API returns HTTP 400 with errors object for validation errors
    const statusCode = data.statusCode || data.status || response.status;

    return {
      data: data,
      statusCode: statusCode
    };
  } catch (error) {
    console.error('Lead Submission Error:', error);
    return {
      data: null,
      statusCode: 500,
      error: error.message
    };
  }
};

/**
 * Handle CRM API response — redirect on success, show error on failure
 * @param {Object} response - Return value from submitLead
 * @param {Function} setError - State setter to display error message
 * @returns {boolean} true if redirected (success)
 */
export const handleLeadResponse = (response, setError) => {
  const { data, statusCode } = response;

  if (statusCode === 200 && data?.data?.customerId) {
    // Success → redirect
    window.location.href = `/thanks/?customerid=${data.data.customerId}`;
    return true;
  }

  if (statusCode === 401) {
    setError(data?.message || 'Unauthorized access');
    return false;
  }

  if (statusCode === 400) {
    // Handle validation errors from API (errors object format)
    if (data?.errors) {
      const errorMessages = Object.values(data.errors).flat().join(', ');
      setError(errorMessages || 'Validation error');
      return false;
    }
    setError(data?.errorMessage || data?.title || 'Invalid submission');
    return false;
  }

  // Fallback
  setError(data?.errorMessage || data?.message || data?.title || 'An error occurred. Please try again.');
  return false;
};
